import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Whenever `key` changes, animates `ref`'s height from what it showed to what its new
 * content needs, then hands the height back to the content (auto).
 */
export function useHeightTransition(
  ref: RefObject<HTMLElement | null>,
  key: unknown,
  durationMs = 300,
) {
  // Kept current by a ResizeObserver, which reports after layout effects: during the effect
  // below it still holds the height from before the change.
  const shown = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      shown.current = el.offsetHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  useLayoutEffect(() => {
    const el = ref.current;
    const from = shown.current;
    if (!el || from === null) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Hold the old height now; measure the new content a frame later, once the tabs have
    // swapped their content (the outgoing panel can still be in the DOM at this point).
    el.style.transition = "none";
    el.style.height = `${from}px`;
    const done = () => {
      el.style.height = "";
      el.style.transition = "";
    };
    // transitionend bubbles: a child's own transition must not end this one early.
    const onEnd = (e: TransitionEvent) => {
      if (e.target === el && e.propertyName === "height") done();
    };
    let timer = 0;
    const frame = requestAnimationFrame(() => {
      el.style.height = "auto";
      const to = el.offsetHeight;
      el.style.height = `${from}px`;
      if (Math.abs(to - from) < 1) return done();
      void el.offsetHeight; // commit the start height before the transition begins
      el.style.transition = `height ${durationMs}ms ease-out`;
      el.style.height = `${to}px`;
      el.addEventListener("transitionend", onEnd);
      timer = window.setTimeout(done, durationMs + 50);
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      el.removeEventListener("transitionend", onEnd);
    };
  }, [key, ref, durationMs]);
}
